import { useCallback, useEffect, useMemo, useState } from 'react';
import { callEdgeFunction, supabase } from './supabaseClient';
import { buildDiscountGroupLookupIndex } from './simpraCheck';
import './UsersAdminPage.css';

const PAGE_SIZE = 40;

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function shortId(id) {
  if (!id) return '—';
  return `${id.slice(0, 8)}…`;
}

function buildDiscountGroupsIndex(groups) {
  const map = new Map();
  (groups || []).forEach((g) => {
    if (!g) return;
    if (g.global_discount_id) {
      map.set(String(g.global_discount_id).toLowerCase(), g);
    }
    // Allow pasting the internal group row id by mistake.
    if (g.id) {
      map.set(String(g.id).toLowerCase(), g);
    }
  });
  return map;
}

function resolveDiscountId(
  rawId,
  simpraDiscounts,
  discountGroupsIndex,
  groupLookup,
) {
  const trimmedId = String(rawId || '').trim();
  if (!trimmedId) return null;

  const simpraMatch = simpraDiscounts?.get(trimmedId);
  if (simpraMatch) {
    const amount = Number(simpraMatch.value);
    return {
      discountId: trimmedId,
      amount: Number.isFinite(amount) ? amount : null,
      type: simpraMatch.type || null,
      name: simpraMatch.translations?.[0]?.name || null,
      isGroup: false,
    };
  }

  const group = discountGroupsIndex.get(trimmedId.toLowerCase());
  if (group) {
    const amount = group.discount_amount != null ? Number(group.discount_amount) : null;
    const memberCount = (group.simpra_discount_group_members || []).length;
    return {
      discountId: group.global_discount_id,
      amount: Number.isFinite(amount) ? amount : null,
      type: group.discount_type || 'percentage',
      name: group.name ? `Grup: ${group.name}` : 'İndirim grubu',
      isGroup: true,
      memberCount,
      members: groupLookup?.getMembersForGlobal(group.global_discount_id) || [],
    };
  }

  const memberHit = groupLookup?.findByPropertyDiscountId(trimmedId);
  if (memberHit) {
    const groupRow = discountGroupsIndex.get(memberHit.globalDiscountId.toLowerCase());
    if (groupRow) {
      const amount = groupRow.discount_amount != null ? Number(groupRow.discount_amount) : null;
      return {
        discountId: groupRow.global_discount_id,
        amount: Number.isFinite(amount) ? amount : null,
        type: groupRow.discount_type || 'percentage',
        name: groupRow.name
          ? `Grup: ${groupRow.name} · ${memberHit.shopLabel || 'şube'}`
          : 'İndirim grubu',
        isGroup: true,
        memberCount: (groupRow.simpra_discount_group_members || []).length,
        members: groupLookup.getMembersForGlobal(groupRow.global_discount_id),
      };
    }
  }

  return null;
}


function DiscountCell({
  userId,
  discountId,
  discountAmount,
  discountType,
  simpraDiscounts,
  discountGroups,
  discountGroupsIndex,
  groupLookup,
  simpraLoading,
  groupsLoading,
  simpraError,
  groupsError,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [dId, setDId] = useState(discountId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDId(discountId || '');
  }, [discountId]);

  const resolved = resolveDiscountId(dId, simpraDiscounts, discountGroupsIndex, groupLookup);

  const save = async () => {
    setError('');
    const trimmedId = dId.trim();

    if (trimmedId) {
      if (simpraLoading || groupsLoading) {
        setError('İndirim listesi yükleniyor, lütfen bekleyin.');
        return;
      }
      if (!resolved?.amount || !resolved?.type) {
        setError(
          simpraError && !groupsError
            ? `Simpra bağlantısı başarısız: ${simpraError}`
            : 'Bu ID Simpra\u2019da veya indirim gruplarında bulunamadı. Simpra sekmesindeki global discount ID kullanın.',
        );
        return;
      }
    }

    setSaving(true);
    try {
      const saveId = trimmedId ? resolved.discountId : null;
      await callEdgeFunction('admin-set-discount-id', {
        method: 'POST',
        body: {
          p_user_id: userId,
          p_discount_id: saveId,
          p_discount_amount: saveId ? resolved.amount : null,
          p_discount_type: saveId ? resolved.type : null,
        },
      });
      onSaved(
        userId,
        saveId,
        saveId ? resolved.amount : null,
        saveId ? resolved.type : null,
      );
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'İndirim kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setError('');
    setSaving(true);
    try {
      await callEdgeFunction('admin-set-discount-id', {
        method: 'POST',
        body: {
          p_user_id: userId,
          p_discount_id: null,
          p_discount_amount: null,
          p_discount_type: null,
        },
      });
      onSaved(userId, null, null, null);
      setDId('');
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'İndirim temizlenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDId(discountId || '');
    setError('');
    setEditing(false);
  };

  if (editing) {
    const trimmed = dId.trim();
    let previewText = '';
    let previewColor = '#94a3b8';
    if (!trimmed) {
      previewText = '';
    } else if (simpraLoading || groupsLoading) {
      previewText = 'İndirim listesi yükleniyor…';
    } else if (resolved?.amount != null && resolved?.type) {
      const label = resolved.type === 'amount' ? `${resolved.amount} TL` : `%${resolved.amount}`;
      const branchNote = resolved.isGroup && resolved.memberCount
        ? ` · ${resolved.memberCount} şube`
        : '';
      previewText = `${label}${branchNote}${resolved.name ? ` — ${resolved.name}` : ''}`;
      previewColor = '#15803d';
    } else if (simpraError && !groupsError) {
      previewText = `Simpra hatası: ${simpraError}`;
      previewColor = '#b91c1c';
    } else {
      previewText = 'Bu ID Simpra veya indirim gruplarında bulunamadı';
      previewColor = '#b91c1c';
    }

    const discountOptions = simpraDiscounts
      ? Array.from(simpraDiscounts.values()).sort((a, b) => {
          const av = Number(a.value) || 0;
          const bv = Number(b.value) || 0;
          if (av !== bv) return av - bv;
          const an = a.translations?.[0]?.name || '';
          const bn = b.translations?.[0]?.name || '';
          return an.localeCompare(bn);
        })
      : [];

    const discountGroupOptions = (discountGroups || []).slice().sort((a, b) => {
      const an = a.name || '';
      const bn = b.name || '';
      return an.localeCompare(bn, 'tr-TR');
    });

    const optionLabel = (d) => {
      const amt = Number(d.value);
      const label = d.type === 'amount' ? `${amt} TL` : `%${amt}`;
      const name = d.translations?.[0]?.name;
      const shopRaw = d.shopLabel || '';
      const shopName = shopRaw.split('·')[0]?.trim() || '';
      const shop = shopName ? ` · ${shopName}` : '';
      return name ? `${label} — ${name}${shop}` : `${label}${shop}`;
    };

    const groupOptionLabel = (g) => {
      const amt = g.discount_amount != null ? Number(g.discount_amount) : null;
      const label = g.discount_type === 'amount'
        ? (amt != null ? `${amt} TL` : 'Tutar')
        : (amt != null ? `%${amt}` : '%');
      const branchCount = (g.simpra_discount_group_members || []).length;
      const branches = branchCount ? ` · ${branchCount} şube` : '';
      return `Grup: ${label} — ${g.name || 'İsimsiz'}${branches}`;
    };

    const selectedValue = resolved?.discountId
      || (simpraDiscounts?.has(trimmed) ? trimmed : '');

    const previewClass = previewColor === '#15803d'
      ? 'users-discount-preview--good'
      : previewColor === '#b91c1c'
        ? 'users-discount-preview--bad'
        : 'users-discount-preview--muted';

    return (
      <div className="users-discount-cell">
        <div>
          <div className="users-discount-label">İndirim</div>
          <select
            className="users-discount-select"
            value={selectedValue}
            onChange={(e) => setDId(e.target.value)}
            disabled={saving || simpraLoading || groupsLoading}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); else if (e.key === 'Enter') save(); }}
          >
            <option value="">
              {simpraLoading || groupsLoading
                ? 'Yükleniyor…'
                : '— İndirim veya grup seçin —'}
            </option>
            {discountGroupOptions.length ? (
              <optgroup label="İndirim grupları (global ID)">
                {discountGroupOptions.map((g) => (
                  <option key={g.id} value={g.global_discount_id}>
                    {groupOptionLabel(g)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {discountOptions.length ? (
              <optgroup label="Simpra (tüm şubeler)">
                {discountOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {optionLabel(d)}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </div>

        <div>
          <div className="users-discount-hint">veya elle UUID girin (global grup ID veya Simpra ID):</div>
          <input
            className="users-discount-input"
            value={dId}
            onChange={(e) => setDId(e.target.value)}
            placeholder="Global grup UUID veya Simpra indirim UUID…"
            disabled={saving}
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); else if (e.key === 'Enter') save(); }}
            style={{ fontFamily: 'ui-monospace, monospace' }}
          />
          {previewText ? (
            <div className={`users-discount-preview ${previewClass}`}>{previewText}</div>
          ) : null}
          {resolved?.isGroup && resolved.members?.length ? (
            <div className="users-discount-branches">
              <p className="users-discount-branches-title">Property indirim ID’leri (global anahtar)</p>
              <ul className="users-discount-branches-list">
                {resolved.members.map((m) => (
                  <li key={`${m.propertyId}-${m.propertyDiscountId}`}>
                    <span>{m.shopLabel || 'Şube'}</span>
                    <code className="users-discount-branches-property" title={m.propertyId}>
                      {m.propertyId}
                    </code>
                    <code title={m.propertyDiscountId}>{m.propertyDiscountId}</code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="users-discount-actions">
          <button type="button" className="users-discount-btn users-discount-btn--primary" onClick={save} disabled={saving}>
            {saving ? '…' : 'Kaydet'}
          </button>
          {discountId ? (
            <button type="button" className="users-discount-btn users-discount-btn--danger" onClick={clear} disabled={saving}>
              Kaldır
            </button>
          ) : null}
          <button type="button" className="users-discount-btn users-discount-btn--ghost" onClick={cancel} disabled={saving}>
            İptal
          </button>
        </div>
        {error ? <span className="users-discount-error">{error}</span> : null}
      </div>
    );
  }

  if (discountId) {
    const live = resolveDiscountId(discountId, simpraDiscounts, discountGroupsIndex, groupLookup);
    const amt = live?.amount ?? discountAmount ?? null;
    const type = live?.type || discountType || 'percentage';
    const label =
      amt == null
        ? '??'
        : type === 'amount'
          ? `${amt} TL`
          : `%${amt}`;
    const groupHint = live?.isGroup ? `\nİndirim grubu: ${live.name}` : '';
    return (
      <button
        type="button"
        className="users-discount-value"
        onClick={() => setEditing(true)}
        title={`Discount ID: ${discountId}${groupHint}\nDüzenlemek için tıkla`}
        style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
      >
        <span className="users-discount-chip users-discount-chip--assigned">{label}</span>
        <span className="users-cell-id">{discountId.slice(0, 8)}…</span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="users-discount-btn users-discount-btn--link"
      onClick={() => setEditing(true)}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14M5 12h14" />
      </svg>
      Ata
    </button>
  );
}

function StatusBadge({ ok, okLabel, badLabel, neutralLabel }) {
  if (ok === null || ok === undefined) {
    return (
      <span className="users-status users-status--neutral">
        {neutralLabel || '—'}
      </span>
    );
  }
  return (
    <span className={`users-status ${ok ? 'users-status--ok' : 'users-status--bad'}`}>
      {ok ? okLabel : badLabel}
    </span>
  );
}

function ToggleSwitch({ checked, onChange, disabled, onLabel = 'Evet', offLabel = 'Hayır' }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '2px 10px 2px 3px', borderRadius: 999,
        background: checked ? '#dcfce7' : '#f1f5f9',
        color: checked ? '#15803d' : '#64748b',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.7rem', fontWeight: 700, transition: 'all 0.15s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: '50%',
        background: checked ? '#15803d' : '#cbd5e1',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', transition: 'all 0.15s',
      }}>
        {checked ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : null}
      </span>
      {checked ? onLabel : offLabel}
    </button>
  );
}

const editInput = {
  padding: '5px 9px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: '0.8rem',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  width: '100%',
  background: '#fff',
};

function MetadataPanel({ user, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState(() => buildDraft(user));

  useEffect(() => {
    if (!editing) setDraft(buildDraft(user));
  }, [user, editing]);

  const {
    id,
    email,
    phone_number: authPhone,
    email_confirmed_at,
    phone_confirmed_at,
    confirmation_sent_at,
    recovery_sent_at,
    invited_at,
    banned_until,
    is_anonymous,
    is_sso_user,
    providers,
    created_at,
    updated_at,
    last_sign_in_at,
    user_metadata: userMeta,
    app_metadata: appMeta,
  } = user || {};

  const skip = new Set([
    'sub', 'email', 'email_verified', 'phone_verified', 'iss',
    // These are exposed as dedicated rows in the edit view, so don't duplicate
    // them in the raw metadata section.
    'full_name', 'phone', 'role',
  ]);
  const filteredUser = Object.entries(userMeta || {}).filter(([k]) => !skip.has(k));
  const filteredApp = Object.entries(appMeta || {}).filter(([k]) => !skip.has(k));

  const bannedActive = banned_until ? new Date(banned_until) > new Date() : false;
  const providerList = Array.isArray(providers) ? providers : [];

  const renderValue = (v) => {
    if (v === null || v === undefined) return <span style={{ color: '#94a3b8' }}>null</span>;
    if (typeof v === 'boolean') return <span style={{ color: v ? '#15803d' : '#b91c1c' }}>{String(v)}</span>;
    if (typeof v === 'object') return <code style={{ fontSize: '0.75rem', color: '#475569' }}>{JSON.stringify(v)}</code>;
    return <span style={{ color: '#1a202c' }}>{String(v)}</span>;
  };

  const renderRawSection = (title, entries) => (
    <div className="users-meta-card">
      <div className="users-meta-card-title">
        <span>{title}</span>
      </div>
      {entries.length === 0 ? (
        <span style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>Boş</span>
      ) : (
        <div className="users-meta-kv">
          {entries.map(([k, v]) => (
            <div key={k} className="users-meta-kv-row">
              <span className="users-meta-kv-key">{k}</span>
              {renderValue(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const save = async () => {
    setErr('');
    const patch = buildPatch(user, draft);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const data = await callEdgeFunction('admin-update-auth-user', {
        method: 'POST',
        body: { p_user_id: id, p_patch: patch },
      });
      if (data && onUpdated) onUpdated(data);
      setEditing(false);
    } catch (e) {
      setErr(e?.message || 'Kayıt güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(buildDraft(user));
    setErr('');
    setEditing(false);
  };

  return (
    <div className="users-meta-panel">
      <div className="users-meta-card users-meta-card--wide">
        <div className="users-meta-card-title">
          <span>Hesap bilgileri</span>
          {editing ? (
            <div className="users-meta-actions">
              <button
                type="button"
                className="users-btn users-btn--primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
              <button
                type="button"
                className="users-btn users-btn--ghost"
                onClick={cancel}
                disabled={saving}
              >
                İptal
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="users-btn--edit"
              onClick={() => setEditing(true)}
              title="Düzenle"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Düzenle
            </button>
          )}
        </div>

        {err ? <div className="users-meta-error">{err}</div> : null}

        <div className="users-meta-grid">
          <span className="users-meta-label">ID</span>
          <code style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#475569', wordBreak: 'break-all' }}>
            {id || '—'}
          </code>

          {/* Email + verified toggle */}
          <span className="users-meta-label">E-posta</span>
          {editing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="email"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                placeholder="email@example.com"
                style={{ ...editInput, maxWidth: 260 }}
              />
              <ToggleSwitch
                checked={draft.email_confirmed}
                onChange={(v) => setDraft((d) => ({ ...d, email_confirmed: v }))}
                onLabel="Doğrulandı"
                offLabel="Doğrulanmadı"
              />
            </div>
          ) : (
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{email || '—'}</span>
              <StatusBadge
                ok={!!email_confirmed_at}
                okLabel="Doğrulandı"
                badLabel="Doğrulanmadı"
              />
              {email_confirmed_at ? (
                <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  {fmtDate(email_confirmed_at)}
                </span>
              ) : null}
            </span>
          )}

          {/* Auth phone + verified toggle */}
          <span className="users-meta-label">Telefon (auth)</span>
          {editing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="tel"
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                placeholder="+905xxxxxxxxx"
                style={{ ...editInput, maxWidth: 200 }}
              />
              <ToggleSwitch
                checked={draft.phone_confirmed}
                onChange={(v) => setDraft((d) => ({ ...d, phone_confirmed: v }))}
                onLabel="Doğrulandı"
                offLabel="Doğrulanmadı"
              />
            </div>
          ) : (
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{authPhone || '—'}</span>
              {authPhone ? (
                <StatusBadge
                  ok={!!phone_confirmed_at}
                  okLabel="Doğrulandı"
                  badLabel="Doğrulanmadı"
                />
              ) : null}
              {phone_confirmed_at ? (
                <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  {fmtDate(phone_confirmed_at)}
                </span>
              ) : null}
            </span>
          )}

          {/* Full name */}
          <span className="users-meta-label">Ad</span>
          {editing ? (
            <input
              value={draft.full_name}
              onChange={(e) => setDraft((d) => ({ ...d, full_name: e.target.value }))}
              placeholder="Ad Soyad"
              style={{ ...editInput, maxWidth: 320 }}
            />
          ) : (
            <span>{user?.full_name || '—'}</span>
          )}

          {/* user_metadata.phone */}
          <span className="users-meta-label">Telefon (profil)</span>
          {editing ? (
            <input
              value={draft.user_phone}
              onChange={(e) => setDraft((d) => ({ ...d, user_phone: e.target.value }))}
              placeholder="Profilde görünen telefon"
              style={{ ...editInput, maxWidth: 260 }}
            />
          ) : (
            <span>{user?.phone || '—'}</span>
          )}

          {/* user role */}
          <span className="users-meta-label">Rol (user)</span>
          {editing ? (
            <input
              value={draft.user_role}
              onChange={(e) => setDraft((d) => ({ ...d, user_role: e.target.value }))}
              placeholder="customer, staff, admin…"
              style={{ ...editInput, maxWidth: 240 }}
            />
          ) : (
            <span>{user?.user_role || '—'}</span>
          )}

          <span className="users-meta-label">İşletme sahibi</span>
          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
            Yalnızca <code style={{ fontSize: '0.8rem' }}>platform_admins</code> tablosu (SQL); metadata ile atanamaz.
          </span>

          {/* Providers (read-only) */}
          <span className="users-meta-label">Sağlayıcılar</span>
          <span>
            {providerList.length === 0 ? (
              <span style={{ color: '#94a3b8' }}>—</span>
            ) : (
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {providerList.map((p) => (
                  <span key={p} className="users-provider-pill">{p}</span>
                ))}
              </span>
            )}
          </span>

          {/* Ban / status */}
          <span className="users-meta-label">Durum</span>
          {editing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <ToggleSwitch
                checked={draft.banned}
                onChange={(v) => setDraft((d) => ({
                  ...d,
                  banned: v,
                  banned_until: v
                    ? (d.banned_until || defaultBanUntil())
                    : '',
                }))}
                onLabel="Banlı"
                offLabel="Aktif"
              />
              {draft.banned ? (
                <input
                  type="datetime-local"
                  value={draft.banned_until}
                  onChange={(e) => setDraft((d) => ({ ...d, banned_until: e.target.value }))}
                  style={{ ...editInput, maxWidth: 220 }}
                />
              ) : null}
            </div>
          ) : (
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {bannedActive
                ? <StatusBadge ok={false} okLabel="" badLabel={`Banlı (${fmtDate(banned_until)})`} />
                : <StatusBadge ok={true} okLabel="Aktif" badLabel="" />}
              {is_anonymous ? <StatusBadge ok={false} okLabel="" badLabel="Anonim" /> : null}
              {is_sso_user ? <StatusBadge ok={true} okLabel="SSO" badLabel="" /> : null}
            </span>
          )}

          {/* Read-only timestamps */}
          <span className="users-meta-label">Kayıt</span>
          <span style={{ color: '#475569' }}>{fmtDate(created_at)}</span>

          <span className="users-meta-label">Güncellenme</span>
          <span style={{ color: '#475569' }}>{fmtDate(updated_at)}</span>

          <span className="users-meta-label">Son giriş</span>
          <span style={{ color: '#475569' }}>{fmtDate(last_sign_in_at)}</span>

          {invited_at ? (
            <>
              <span className="users-meta-label">Davet edildi</span>
              <span style={{ color: '#475569' }}>{fmtDate(invited_at)}</span>
            </>
          ) : null}
          {confirmation_sent_at ? (
            <>
              <span className="users-meta-label">Doğr. gönderildi</span>
              <span style={{ color: '#475569' }}>{fmtDate(confirmation_sent_at)}</span>
            </>
          ) : null}
          {recovery_sent_at ? (
            <>
              <span className="users-meta-label">Kurtarma gönd.</span>
              <span style={{ color: '#475569' }}>{fmtDate(recovery_sent_at)}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Raw metadata sections stay read-only */}
      {renderRawSection('user_metadata', filteredUser)}
      {renderRawSection('app_metadata', filteredApp)}
    </div>
  );
}

// ───────────────── Helpers for the edit form ─────────────────

function buildDraft(u) {
  const banned = !!(u?.banned_until && new Date(u.banned_until) > new Date());
  return {
    email: u?.email || '',
    phone: u?.phone_number || '',
    email_confirmed: !!u?.email_confirmed_at,
    phone_confirmed: !!u?.phone_confirmed_at,
    full_name: u?.full_name || '',
    user_phone: u?.phone || '',
    user_role: u?.user_role || '',
    banned,
    banned_until: banned ? toLocalDatetime(u.banned_until) : '',
  };
}

// Diff current user against draft; only include keys that actually changed so
// the RPC can treat "absent" as "don't touch".
function buildPatch(u, d) {
  const patch = {};
  const origEmail = u?.email || '';
  const origPhone = u?.phone_number || '';
  const origFullName = u?.full_name || '';
  const origUserPhone = u?.phone || '';
  const origUserRole = u?.user_role || '';
  const origEmailConfirmed = !!u?.email_confirmed_at;
  const origPhoneConfirmed = !!u?.phone_confirmed_at;
  const origBanned = !!(u?.banned_until && new Date(u.banned_until) > new Date());

  if (d.email.trim() !== origEmail) patch.email = d.email.trim();
  if (d.phone.trim() !== origPhone) patch.phone = d.phone.trim();
  if (d.full_name.trim() !== origFullName) patch.full_name = d.full_name.trim();
  if (d.user_phone.trim() !== origUserPhone) patch.user_phone = d.user_phone.trim();
  if (d.user_role.trim() !== origUserRole) patch.user_role = d.user_role.trim();
  if (d.email_confirmed !== origEmailConfirmed) patch.email_confirmed = d.email_confirmed;
  if (d.phone_confirmed !== origPhoneConfirmed) patch.phone_confirmed = d.phone_confirmed;

  if (d.banned !== origBanned
      || (d.banned && d.banned_until && toLocalDatetime(u?.banned_until) !== d.banned_until)) {
    if (!d.banned) {
      patch.banned_until = null;
    } else if (d.banned_until) {
      patch.banned_until = new Date(d.banned_until).toISOString();
    }
  }
  return patch;
}

function toLocalDatetime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultBanUntil() {
  // Default ban length: +100 years from now (effectively permanent) but still
  // editable via the datetime picker if the admin wants a temporary ban.
  const d = new Date();
  d.setFullYear(d.getFullYear() + 100);
  return toLocalDatetime(d.toISOString());
}

export default function UsersAdminPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const [simpraDiscountsList, setSimpraDiscountsList] = useState([]);
  const [simpraLoading, setSimpraLoading] = useState(true);
  const [simpraError, setSimpraError] = useState('');
  const [discountGroupsList, setDiscountGroupsList] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');

  const simpraDiscounts = useMemo(() => {
    const map = new Map();
    (simpraDiscountsList || []).forEach((d) => {
      if (d && d.id) map.set(d.id, d);
    });
    return map;
  }, [simpraDiscountsList]);

  const discountGroupsIndex = useMemo(
    () => buildDiscountGroupsIndex(discountGroupsList),
    [discountGroupsList],
  );

  const groupLookup = useMemo(
    () => buildDiscountGroupLookupIndex(discountGroupsList),
    [discountGroupsList],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSimpraLoading(true);
      setSimpraError('');
      try {
        const res = await callEdgeFunction('simpra-proxy', {
          method: 'POST',
          body: { method: 'GET', path: '/discounts' },
        });
        if (cancelled) return;
        const list = Array.isArray(res?.discounts) ? res.discounts : [];
        setSimpraDiscountsList(list);
      } catch (e) {
        if (cancelled) return;
        setSimpraError(e?.message || 'Simpra indirim listesi alınamadı.');
        setSimpraDiscountsList([]);
      } finally {
        if (!cancelled) setSimpraLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setGroupsLoading(true);
      setGroupsError('');
      try {
        const { data, error } = await supabase
          .from('simpra_discount_groups')
          .select(`
            id,
            name,
            global_discount_id,
            discount_amount,
            discount_type,
            simpra_discount_group_members (
              property_id,
              property_discount_id,
              shop_label
            )
          `)
          .order('name', { ascending: true });
        if (cancelled) return;
        if (error) {
          if (error.message.includes('simpra_discount_groups')) {
            throw new Error('simpra_discount_groups yok. sql/84_simpra_discount_groups.sql çalıştırın.');
          }
          throw error;
        }
        setDiscountGroupsList(Array.isArray(data) ? data : []);
      } catch (e) {
        if (cancelled) return;
        setGroupsError(e?.message || 'İndirim grupları yüklenemedi.');
        setDiscountGroupsList([]);
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await callEdgeFunction('admin-list-auth-users', {
        method: 'POST',
        body: {
          p_limit: PAGE_SIZE,
          p_offset: page * PAGE_SIZE,
          p_search: searchDebounced || null,
        },
      });
      let payload = {};
      if (typeof data === 'string') {
        try {
          payload = JSON.parse(data);
        } catch {
          payload = {};
        }
      } else if (data && typeof data === 'object') {
        payload = data;
      }
      setTotal(Number(payload.total) || 0);
      setRows(Array.isArray(payload.users) ? payload.users : []);
    } catch (e) {
      setError(e?.message || 'Kullanicilar yuklenemedi.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUserUpdated = (updatedUser) => {
    if (!updatedUser || !updatedUser.id) return;
    setRows((prev) => prev.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)));
  };

  const handleDiscountSaved = (userId, newId, newAmount, newType) => {
    setRows((prev) =>
      prev.map((u) => {
        if (u.id !== userId) return u;
        const updatedMeta = { ...(u.user_metadata || {}) };
        if (newId) {
          updatedMeta.discount_id = newId;
          updatedMeta.discount_amount = newAmount;
          updatedMeta.discount_type = newType;
        } else {
          delete updatedMeta.discount_id;
          delete updatedMeta.discount_amount;
          delete updatedMeta.discount_type;
        }
        return { ...u, discount_id: newId, user_metadata: updatedMeta };
      }),
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="admin-page-content users-page">
      <header className="users-hero">
        <div className="users-hero-copy">
          <p className="users-kicker">Yönetim</p>
          <h1 className="users-title">Kullanıcılar</h1>
          <p className="users-sub">
            Hesaplar, roller, doğrulama durumu ve Simpra indirim atamaları.
          </p>
        </div>
      </header>

      {error ? (
        <p className="users-alert users-alert--error" role="alert">{error}</p>
      ) : null}

      {simpraError ? (
        <p className="users-alert users-alert--warn">
          Simpra indirim listesi alınamadı: {simpraError}. Tek şube indirimleri
          doğrulanamayacak; indirim grupları hâlâ atanabilir.
        </p>
      ) : null}

      {groupsError ? (
        <p className="users-alert users-alert--warn">
          İndirim grupları yüklenemedi: {groupsError}. Global grup ID ile atama yapılamaz.
        </p>
      ) : null}

      <div className="users-summary" aria-label="Arama ve özet">
        <div className="users-summary-search">
          <svg className="users-summary-search-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="users-summary-search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="E-posta, ad, telefon…"
            aria-label="Kullanıcı ara"
          />
        </div>
        <p className="users-summary-meta" aria-live="polite">
          <span className="users-summary-total">{total.toLocaleString('tr-TR')}</span>
          <span className="users-summary-label">
            kullanıcı
            {searchDebounced.trim() ? ` · “${searchDebounced.trim()}”` : ''}
          </span>
        </p>
        <button type="button" className="users-refresh" onClick={load} disabled={loading}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 4.36A9 9 0 0020.49 15" />
          </svg>
          {loading ? 'Yükleniyor…' : 'Yenile'}
        </button>
      </div>

      <div className="users-table-card">
        <div className="users-table-scroll">
        <table className="users-table">
          <thead>
            <tr>
              <th className="users-col-expand" aria-label="Detay" />
              <th>E-posta</th>
              <th>Ad</th>
              <th>Telefon</th>
              <th>Rol (user)</th>
              <th>Rol (app)</th>
              <th>İndirim</th>
              <th>Kayıt</th>
              <th>Son giriş</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="users-empty">
                  Yükleniyor…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="users-empty">
                  Kayıt yok.
                </td>
              </tr>
            ) : (
              rows.map((u) => {
                const isExpanded = expandedId === u.id;
                const isBanned = !!(u.banned_until && new Date(u.banned_until) > new Date());
                return [
                  <tr
                    key={u.id}
                    className={`users-table-row${isExpanded ? ' users-table-row--open' : ''}${isBanned ? ' users-table-row--banned' : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : u.id)}
                  >
                    <td className="users-col-expand">
                      <span className={`users-expand-icon${isExpanded ? ' users-expand-icon--open' : ''}`} aria-hidden>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </span>
                    </td>
                    <td className="users-cell-email">
                      <span className="users-email-line">
                        {u.email || '—'}
                        {u.email_confirmed_at ? (
                          <span title={`Doğrulandı: ${fmtDate(u.email_confirmed_at)}`} className="users-badge users-badge--verified">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        ) : (
                          <span title="E-posta doğrulanmamış" className="users-badge users-badge--warn">!</span>
                        )}
                        {isBanned ? (
                          <span title={`Banlı: ${fmtDate(u.banned_until)}`} className="users-badge users-badge--ban">BAN</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="users-cell-name">{u.full_name || '—'}</td>
                    <td className="users-cell-phone">{u.phone || '—'}</td>
                    <td>
                      {u.user_role ? (
                        <span className="users-role-pill">{u.user_role}</span>
                      ) : '—'}
                    </td>
                    <td>
                      {u.app_role ? (
                        <span className="users-role-pill users-role-pill--app">{u.app_role}</span>
                      ) : '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <DiscountCell
                        userId={u.id}
                        discountId={u.discount_id}
                        discountAmount={u.user_metadata?.discount_amount}
                        discountType={u.user_metadata?.discount_type}
                        simpraDiscounts={simpraDiscounts}
                        discountGroups={discountGroupsList}
                        discountGroupsIndex={discountGroupsIndex}
                        groupLookup={groupLookup}
                        simpraLoading={simpraLoading}
                        groupsLoading={groupsLoading}
                        simpraError={simpraError}
                        groupsError={groupsError}
                        onSaved={handleDiscountSaved}
                      />
                    </td>
                    <td className="users-cell-date">{fmtDate(u.created_at)}</td>
                    <td className="users-cell-date">{fmtDate(u.last_sign_in_at)}</td>
                    <td title={u.id} className="users-cell-id">
                      {shortId(u.id)}
                    </td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${u.id}-meta`} className="users-meta-row">
                      <td colSpan={10} className="users-meta-cell">
                        <div onClick={(e) => e.stopPropagation()}>
                          <MetadataPanel user={u} onUpdated={handleUserUpdated} />
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })
            )}
          </tbody>
        </table>
        </div>

        {totalPages > 1 ? (
          <nav className="users-pagination" aria-label="Sayfalama">
            <button
              type="button"
              className="users-page-btn"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Önceki
            </button>
            <span className="users-page-indicator">
              Sayfa {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="users-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Sonraki
            </button>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
